/**
 * Where everything sits on an assembly sheet.
 *
 * Pure geometry: it takes the backend's `AssemblyDrawingSpec` and returns the millimetre
 * coordinates the SVG draws at. Every constant below is copied from the backend renderer
 * (`ga_kasko_eda_be/assemblies/visualization.py`) so the native drawing lands on the same
 * layout as the PNG the user can download — same column spacing, same diameter block,
 * same margins.
 *
 * Coordinates stay in the drawing's own frame (x across, z up, millimetres). The SVG flips
 * z on the way out by negating it, rather than by a `scale(1,-1)` group, so that labels
 * are never mirrored.
 */

import type { AssemblyDrawingSpec, DiameterDimension, VerticalDimension } from '@/types/assemblyDrawing.types';

/** How far apart the dimension columns march away from the axis. */
const DIM_COLUMN_STEP_MM = 12;

/**
 * The drawing area of the backend figure, in inches — `_DRAWING_RECT` of a
 * `_FIGURE_SIZE_IN` page. Only its size matters here: it is what turns the renderer's
 * point-based type scale and line widths into millimetres of drawing space.
 */
const SHEET_WIDTH_IN = 0.61 * 12.5;
const SHEET_HEIGHT_IN = 0.875 * 11;

/** Type scale and stroke weights of the sheet, in points (as in the backend renderer). */
export const DIM_FONT_PT = 7.5;
export const DIM_LABEL_OFFSET_PT = 3;
export const ARROW_HEAD_PT = 4;
export const BODY_LINE_PT = 0.8;
export const DIM_LINE_PT = 0.9;
export const LEADER_LINE_PT = 0.7;
export const GAUGE_LINE_PT = 1.1;
export const AXIS_LINE_PT = 0.7;

/**
 * Flat slate/steel palette of the backend sheet: muted fills carried by stronger edges,
 * with the cutting part as the one saturated accent so the eye goes there first.
 */
export const DRAWING_COLORS = {
  holder: '#94a3b8',
  holderEdge: '#334155',
  extension: '#c08a3e',
  extensionEdge: '#5c3d0f',
  tool: '#e2e8f0',
  toolEdge: '#475569',
  cutting: '#fbbf24',
  cuttingEdge: '#b45309',
  dim: '#475569',
  dimLeader: '#94a3b8',
  gauge: '#dc2626',
  axis: '#cbd5e1',
};

/** One vertical dimension with the x of the column it was placed in. */
export interface PlacedVerticalDimension {
  dim: VerticalDimension;
  x: number;
}

/** One diameter dimension with the height of the row it was placed on. */
export interface PlacedDiameterDimension {
  dim: DiameterDimension;
  z: number;
}

/** The finished layout of one sheet, in drawing millimetres. */
export interface DrawingLayout {
  /** Half-width of the widest body — where the dimension columns start from. */
  maxR: number;
  /** Highest point of the assembly. */
  top: number;
  gaugeZ: number;
  verticalDims: PlacedVerticalDimension[];
  diameterDims: PlacedDiameterDimension[];
  /** Height of the notes line under the diameter block, or `null` when there are none. */
  notesZ: number | null;
  /** Gap between a vertical arrow's top and its rotated label. */
  labelPad: number;
  /** SVG viewBox of the whole sheet, y already flipped (`y = -z`). */
  view: { x: number; y: number; width: number; height: number };
  /**
   * One typographic point in millimetres of drawing space. The sheet is fitted into a
   * fixed-size area, so a point size only becomes a length once the extent is known.
   */
  mmPerPoint: number;
}

/**
 * Place every element of one sheet.
 *
 * The columns start one step off the widest body and march outward in the order the
 * specification lists them; the diameter block hangs under the tip, one row per diameter,
 * with a step wide enough to keep a label clear of the arrow above it.
 */
export function buildDrawingLayout(spec: AssemblyDrawingSpec): DrawingLayout {
  const { extents, gauge_z: gaugeZ } = spec;
  const maxR = Math.max(Math.abs(extents.x_min), Math.abs(extents.x_max));
  const top = extents.z_max;
  const labelPad = Math.max(2, top * 0.012);

  const verticalDims: PlacedVerticalDimension[] = [];
  let leftCount = 0;
  let rightCount = 0;
  for (const dim of spec.dims_vertical) {
    const direction = dim.side === 'left' ? -1 : 1;
    const index = dim.side === 'left' ? leftCount++ : rightCount++;
    verticalDims.push({ dim, x: direction * (maxR + DIM_COLUMN_STEP_MM * (index + 1)) });
  }
  const xLeft = -(maxR + DIM_COLUMN_STEP_MM * Math.max(1, leftCount));
  const xRight = maxR + DIM_COLUMN_STEP_MM * Math.max(1, rightCount);

  // The floors below come from the tightest case the catalog has — a tool whose diameter,
  // neck and shank all want a row.
  const diameterStep = Math.max(8, gaugeZ * 0.05);
  let z = -Math.max(10, gaugeZ * 0.06);
  const diameterDims: PlacedDiameterDimension[] = spec.dims_diameter.map((dim, index) => {
    if (index) z -= diameterStep;
    return { dim, z };
  });
  let notesZ: number | null = null;
  if (spec.notes.length > 0) {
    z -= diameterStep;
    notesZ = z;
  }

  const xMin = xLeft - 30;
  const xMax = xRight + 40;
  const zMin = z - diameterStep;
  const zMax = Math.max(top, gaugeZ) + 12 + 0.12 * gaugeZ;

  const width = xMax - xMin;
  const height = zMax - zMin;
  const mmPerInch = Math.max(width / SHEET_WIDTH_IN, height / SHEET_HEIGHT_IN);

  return {
    maxR,
    top,
    gaugeZ,
    verticalDims,
    diameterDims,
    notesZ,
    labelPad,
    view: { x: xMin, y: -zMax, width, height },
    mmPerPoint: mmPerInch / 72,
  };
}

/** Turn one closed body outline into SVG path data, flipping z to screen y. */
export function polygonToPath(polygon: [number, number][]): string {
  if (polygon.length === 0) return '';
  return `${polygon.map(([x, pz], index) => `${index === 0 ? 'M' : 'L'}${x} ${-pz}`).join(' ')} Z`;
}
